import { isStudioEarlyAccessFeature, type FeatureKey } from "@/lib/authz/policy";

/**
 * Bundle membership — must stay in sync with the server-side mapping in
 * `src/lib/server/sealSubscriptions.ts`. Kept here as the client-safe
 * source of truth so we don't need to import server code.
 */
const VEHICLE_BUNDLE: FeatureKey[] = [
  "paintbooth",
  "liverymagic",
  "uvstudio",
  "blinklab",
  "metaroom",
  "chevronbuilder",
  "creatorconvert",
];

const SERVER_BUNDLE: FeatureKey[] = [
  "packbench",
  "codesmith",
  "codevisualizer",
  "scriptdocs",
  "eupviewer",
  "addoneupcreator",
  "weaponviewer",
  "itemimage",
  "embroiderytool",
  "creatorconvert",
];

export type PlanLabel =
  | "Admin"
  | "Pro"
  | "Vehicle Developer"
  | "Server Developer"
  | "Paintbooth"
  | "Custom"
  | "Free";

type Permissions = Partial<Record<FeatureKey, boolean>>;

export function derivePlanLabel(
  permissions: Permissions | undefined,
  options: { isAdmin?: boolean } = {},
): PlanLabel {
  if (options.isAdmin) return "Admin";
  if (!permissions) return "Free";

  const has = (key: FeatureKey) => permissions[key] === true;
  const hasAll = (keys: FeatureKey[]) => keys.every(has);

  const hasVehicle = hasAll(VEHICLE_BUNDLE);
  const hasServer = hasAll(SERVER_BUNDLE);

  if (hasVehicle && hasServer) return "Pro";
  if (hasVehicle) return "Vehicle Developer";
  if (hasServer) return "Server Developer";
  if (has("paintbooth")) return "Paintbooth";

  // Any granted feature outside the canonical tiers — surface as "Custom"
  // so the user knows it's a one-off SKU rather than an empty subscription.
  const grantedCount = Object.values(permissions).filter(Boolean).length;
  if (grantedCount > 0) return "Custom";
  return "Free";
}

/**
 * Destination for a *locked* tool's CTA. Studio early-access tools (e.g.
 * Embroidery) route to the access-denied paywall, which frames them as a
 * "Studio-only for now" perk; every other locked tool goes to the generic
 * subscription page where the matching plan can be added.
 */
export function lockedToolHref(feature: FeatureKey | undefined): string {
  if (feature && isStudioEarlyAccessFeature(feature)) {
    return `/access-denied?tool=${encodeURIComponent(feature)}`;
  }
  return "/subscription";
}

/** Convenience for callers that already have the auth context handy. */
export function hasFeature(
  permissions: Permissions | undefined,
  feature: FeatureKey | undefined,
  options: { isAdmin?: boolean } = {},
): boolean {
  if (options.isAdmin) return true;
  if (!feature) return true; // Tools without a feature gate are always free.
  return Boolean(permissions?.[feature]);
}
